import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigationContext } from 'Organisms/NavigationWithContext/NavigationContext';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import Portal from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Portal';
import fr from 'facet-rest';
import { FDEmphasisSurface, FDStack } from '@facet-wealth/design';
import { pick } from 'Helpers/Affordances';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import FullScreenDataContainer
  from 'Components/ComponentLibrary/Components/Uncategorized/DataContainer/FullScreenDataContainer';
import NullComponent from 'Components/UI/NullComponent';
import { NavItems } from './navItems';

const useStyles = buildUseStyles('GuidedHouseholdReviewLeftNav', (theme) => ({
  background: {
    backgroundColor: theme.palette.facet.primaryBlue,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    height: '100%',
    width: '100%',
  },
  loader: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    '& .MuiCircularProgress-svg': {
      color: theme.palette.common.white,
    },
  },
}));

export const GuidedHouseholdReviewLeftNav = ({
  showGoals, setShowGoals, pathResource, size,
}) => {
  const classes = useStyles();
  const { navPortal, navigationResource, setOpen } = useNavigationContext();
  const { loadModalCollectionByLink, isInitializing } = useModalContext();
  const { displayErrorMessage } = useSnackbarContext();
  const { loadPageContentLink, isPageContentLoading } = usePageContentContext();
  const closeNav = useCallback(() => setOpen(false), [setOpen]);

  const viewAssumptions = async () => {
    try {
      await loadModalCollectionByLink(pathResource?.relationship(fr.relationships.assumptions.name).link('related').url());
      closeNav();
    } catch (err) {
      displayErrorMessage(err, 'Failed to open assumptions');
    }
  };
  const toggleGoalsView = () => {
    setShowGoals(!showGoals);
    closeNav();
  };

  const computeOnClickFor = (item) => pick(item, {
    [fr.relationships.toggleGoalsView.name]: toggleGoalsView,
    [fr.relationships.viewAssumptions.name]: viewAssumptions,
  }, async () => {
    const relatedLink = item.relationship(fr.relationships.navigate.name)?.link('related');
    if (!relatedLink) {
      return;
    }
    await loadPageContentLink(relatedLink);
    closeNav();
  });
  const computeTitleFor = (item) => pick(item, {
    [fr.relationships.toggleGoalsView.name]: showGoals ? 'Switch to Chart View' : 'Switch to Goals View',
  }, item.attr('title'));

  if (!navPortal) {
    return null;
  }

  return (
    <Portal container={() => navPortal}>
      <div className={classes.loader}>
        <FullScreenDataContainer
          component={NullComponent}
          loading={isInitializing || isPageContentLoading}
        />
      </div>
      <FDEmphasisSurface flex={1} height="100%" rounded={false}>
        <div className={classes.background}>
          <FDStack direction="column" spacing={3} flex={1} justifyContent="space-between">
            <FDStack direction="column" spacing={2} flex={1}>
              <NavItems
                items={navigationResource?.attr('children')?.resources()?.list() ?? []}
                computeOnClickFor={computeOnClickFor}
                computeTitleFor={computeTitleFor}
                size={size}
              />
            </FDStack>
          </FDStack>
        </div>
      </FDEmphasisSurface>
    </Portal>
  );
};

GuidedHouseholdReviewLeftNav.propTypes = {
  showGoals: PropTypes.bool,
  setShowGoals: PropTypes.func,
  pathResource: PropTypes.object,
  size: PropTypes.oneOf(['md', 'lg']),
};
